import React, { useState, useEffect } from 'react';
import { Card, Button, List, Typography, Modal, Space, message, notification, Spin } from 'antd';
import { EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { MemberProfile } from '../../../model/members/MemberProfile';
import { useMemberService } from '../../../services/members/MemberServiceProvider';
import { useMember } from '../../../common/member/MemberContext';
import MemberSearchBar from '../../common/MemberSearchBar';
import { AdminPermissions } from '../../../model/auth/Permissions';
import { UpdateMemberRequest } from '../../../requests/member/UpdateMemberRequest';
import { toDateOnly } from '../../../utils/DateOnly';

const { Title, Text } = Typography;

interface JointMembershipManagerProps {
    member: MemberProfile;
    onUpdate: (values: Partial<MemberProfile>) => void;
    backgroundColor?: string;
    borderColor?: string;
}

const JointMembershipManager: React.FC<JointMembershipManagerProps> = ({
    member,
    onUpdate,
    backgroundColor = '#ffffff',
    borderColor = '#e5e7eb'
}) => {
    const [jointMembers, setJointMembers] = useState<MemberProfile[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingJointMembers, setEditingJointMembers] = useState<MemberProfile[]>([]);
    const [hasChanges, setHasChanges] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    
    const memberService = useMemberService();
    const { permissions, isLoading: permissionsLoading } = useMember();

    // Check if user has admin or front of house permissions
    const hasEditPermission = () => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === AdminPermissions.ClubAdmin ||
            p.name === AdminPermissions.FortiusAdmin ||
            p.name === AdminPermissions.FrontOfHouse
        );
    };

    // Load joint members on component mount and when joint membership IDs change
    useEffect(() => {
        const loadJointMembers = async () => {
            // Prevent multiple simultaneous requests
            if (isLoading) {
                return;
            }

            let jointMembershipIds = member.metadata?.JointMembershipIds;
            
            console.log('Original jointMembershipIds:', jointMembershipIds);
            console.log('Type of jointMembershipIds:', typeof jointMembershipIds);
            
            // Handle case where JointMembershipIds might be a JSON string
            if (typeof jointMembershipIds === 'string') {
                try {
                    jointMembershipIds = JSON.parse(jointMembershipIds);
                    console.log('After JSON.parse:', jointMembershipIds);
                } catch (error) {
                    console.error('Error parsing JointMembershipIds:', error);
                    setJointMembers([]);
                    return;
                }
            }
            
            console.log('Final jointMembershipIds before API call:', jointMembershipIds);
            
            if (!jointMembershipIds || jointMembershipIds.length === 0) {
                setJointMembers([]);
                return;
            }

            try {
                setIsLoading(true);
                const members = await memberService.getMembersByIds(jointMembershipIds);
                setJointMembers(members);
            } catch (error) {
                console.error('Error loading joint members:', error);
                notification.error({
                    message: 'Error',
                    description: 'Failed to load joint members',
                });
            } finally {
                setIsLoading(false);
            }
        };

        loadJointMembers();
        // isLoading is read only as a concurrency guard; including it would re-run
        // the effect on every load start/finish. memberService is stable.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [member.metadata?.JointMembershipIds, memberService]);

    const handleEditClick = () => {
        setEditingJointMembers([...jointMembers]);
        setHasChanges(false);
        setIsModalVisible(true);
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setEditingJointMembers([]);
        setHasChanges(false);
    };

    const handleMemberSelect = (selectedMember: MemberProfile) => {
        // Check if member is already in the list
        const isAlreadyAdded = editingJointMembers.some(
            member => member.membershipId === selectedMember.membershipId
        );

        if (isAlreadyAdded) {
            message.warning('This member is already in the joint membership');
            return;
        }

        // Check if trying to add the current member
        if (selectedMember.membershipId === member.membershipId) {
            message.warning('Cannot add the current member to their own joint membership');
            return;
        }

        setEditingJointMembers([...editingJointMembers, selectedMember]);
        setHasChanges(true);
    };

    const handleRemoveMember = (membershipId: string) => {
        setEditingJointMembers(editingJointMembers.filter(m => m.membershipId !== membershipId));
        setHasChanges(true);
    };

    const handleSave = async () => {
        try {
            const jointMembershipIds = editingJointMembers.map(m => m.membershipId);
            
            const updateRequest: UpdateMemberRequest = {
                name: member.name,
                username: member.username,
                dateOfBirth: member.dateOfBirth,
                emailAddress: member.emailAddress,
                addressLine1: member.addressLine1,
                addressLine2: member.addressLine2,
                townCity: member.townCity,
                countyState: member.countyState,
                postZipCode: member.postZipCode,
                phoneNumber: member.phoneNumber,
                membershipId: member.membershipId,
                specialOffers: member.specialOffers,
                metadata: {
                    ...member.metadata,
                    JointMembershipIds: jointMembershipIds
                },
                membershipCategory: member.membershipCategory,
                validTo: toDateOnly(member.validTo),
                validFrom: toDateOnly(member.validFrom),
                isEnabled: member.isEnabled,
                clientName: member.metadata?.ClientName,
                auth0Id: member.auth0Id,
                status: member.status,
                country: member.country,
                changeType: 'Update' as const
            };

            setIsLoading(true);
            await memberService.update(updateRequest);
            setIsLoading(false);
            
            message.success('Joint membership updated successfully');
            setJointMembers(editingJointMembers);
            setIsModalVisible(false);
            setHasChanges(false);
            
            // Refresh the parent component
            const updatedMember = {
                ...member,
                metadata: {
                    ...member.metadata,
                    JointMembershipIds: jointMembershipIds
                }
            };

            if (jointMembershipIds.length > 0 && !jointMembershipIds.includes(member.membershipId)) {
                jointMembershipIds.push(member.membershipId);
                updatedMember.metadata.JointMembershipIds = jointMembershipIds;
            }
            onUpdate(updatedMember);
        } catch (error) {
            console.error('Error updating joint membership:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to update joint membership',
            });
        }
    };

    const renderJointMember = (jointMember: MemberProfile) => (
        <List.Item>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <div style={{ flex: 1 }}>
                    <Title level={5} style={{ margin: 0 }}>
                        {jointMember.name}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '0.9em' }}>
                        {jointMember.emailAddress} • {jointMember.membershipId}
                    </Text>
                </div>
            </div>
        </List.Item>
    );

    const renderEditingJointMember = (jointMember: MemberProfile) => (
        <List.Item
            actions={[
                <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveMember(jointMember.membershipId)}
                />
            ]}
        >
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <div style={{ flex: 1 }}>
                    <Title level={5} style={{ margin: 0 }}>
                        {jointMember.name}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '0.9em' }}>
                        {jointMember.emailAddress} • {jointMember.membershipId}
                    </Text>
                </div>
            </div>
        </List.Item>
    );

    return (
        <>
            <Spin spinning={isLoading} tip="Loading joint members...">
                <Card
                    title={
                        <Space>
                            <UserOutlined />
                            <span>Joint Membership</span>
                            {hasEditPermission() && (
                                <Button
                                    type="primary"
                                    icon={<EditOutlined />}
                                    size="small"
                                    onClick={handleEditClick}
                                >
                                    Edit
                                </Button>
                            )}
                        </Space>
                    }
                    className="mb-4"
                    style={{
                        backgroundColor,
                        borderColor
                    }}
                >
                    {jointMembers.length === 0 ? (
                        <Text type="secondary">No joint members found</Text>
                    ) : (
                        <List
                            dataSource={jointMembers}
                            renderItem={renderJointMember}
                            size="small"
                        />
                    )}
                </Card>
            </Spin>

            <Modal
                title="Edit Joint Membership"
                open={isModalVisible}
                onCancel={handleModalCancel}
                footer={[
                    <Button key="cancel" onClick={handleModalCancel}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleSave}
                        disabled={!hasChanges}
                        loading={isLoading}
                    >
                        Save Changes
                    </Button>
                ]}
                width={600}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div>
                        <Title level={5}>Add Joint Members</Title>
                        <MemberSearchBar
                            onSelect={handleMemberSelect}
                            onRefresh={() => {}}
                            onMemberSelect={handleMemberSelect}
                        />
                    </div>

                    {editingJointMembers.length > 0 && (
                        <div>
                            <Title level={5}>Joint Members ({editingJointMembers.length})</Title>
                            <List
                                dataSource={editingJointMembers}
                                renderItem={renderEditingJointMember}
                                size="small"
                            />
                        </div>
                    )}
                </Space>
            </Modal>
        </>
    );
};

export default JointMembershipManager; 